import { http } from "../../utils/config";

const organizationAPI = {
  // Get all with pagination and search
  getOrganizations: (params = {}) => {
    const {
      page = 1,
      per_page = 15,
      search = "",
      status = "",
      // Backend mặc định natural sort theo số trong tên (khu phố tăng dần)
      sort_by = "name",
      sort_order = "asc",
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
      sort_by,
      sort_order,
      ...(search && { search }),
      ...(status !== "" && { status }),
    });

    return http.get(`/api/v1/organizations?${queryParams}`);
  },

  // Get by ID
  getOrganizationById: (id) => {
    return http.get(`/api/v1/organizations/${id}`);
  },

  // Create new
  createOrganization: (organizationData) => {
    return http.post("/api/v1/organizations", organizationData);
  },

  // Update
  updateOrganization: (id, organizationData) => {
    return http.put(`/api/v1/organizations/${id}`, organizationData);
  },

  // Delete
  deleteOrganization: (id) => {
    return http.delete(`/api/v1/organizations/${id}`);
  },

  // Update status
  updateStatus: (id, status) => {
    return http.patch(`/api/v1/organizations/${id}/status`, {
      status: status,
    });
  },

  // Get tree
  getTreeStructure: () => {
    return http.get(`/api/v1/organizations/tree/structure`);
  },

  getOrgCustomers: (id, params = {}) => {
    const {
      page = 1,
      per_page = 15,
      search = "",
      sort_by = "created_at",
      sort_order = "desc",
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
      sort_by,
      sort_order,
      ...(search && { search }),
    });

    return http.get(`/api/v1/organizations/${id}/customers?${queryParams}`);
  },

  // them khach hang vao he thong
  attachCustomers: (id, customerData) => {
    return http.post(`/api/v1/organizations/${id}/customers/attach`, customerData);
  },

  // go khach hang vao he thong
  detachCustomers: (id, customerData) => {
    return http.post(`/api/v1/organizations/${id}/customers/detach`, customerData);
  },

  // Khách hàng còn thêm được vào đơn vị. Backend đã loại sẵn người đã thuộc đơn vị và
  // người của đơn vị khác, nên màn hình không phải tự trừ danh sách nữa.
  getAvailableCustomers: (id, params = {}) => {
    return http.get(`/api/v1/organizations/${id}/customers/available`, { params });
  },

  getOrgCollections: (id, params = {}) => {
    const {
      page = 1,
      per_page = 15,
      search = "",
      sort_by = "created_at",
      sort_order = "desc",
    } = params;

    const queryParams = new URLSearchParams({
      page: page.toString(),
      per_page: per_page.toString(),
      sort_by,
      sort_order,
      ...(search && { search }),
    });

    return http.get(`/api/v1/organizations/${id}/collections?${queryParams}`);
  },

  getTotalCollections: (id) => {
    return http.get(`/api/v1/organizations/${id}/total-collections`);
  },

  getCustomerAllByOrg: (id, params = {}) => {
    return http.get(`/api/v1/organizations/${id}/customer/all`, { params });
  },

  // Khách hàng của NHIỀU đơn vị trong 1 request.
  // Trước đây màn sửa đợt thu gọi getCustomerAllByOrg tuần tự cho từng đơn vị — phường
  // ~40 khu phố là 40 vòng đi-về kèm 40 preflight CORS, đo thực tế >80s mới mở được trang.
  getCustomerAllByOrgs: (orgIds = [], params = {}) => {
    return http.get('/api/v1/organizations/customers/all-by-orgs', {
      params: { ...params, organization_ids: orgIds },
    });
  }

};

// Helper functions for handling API responses
export const organizationHelpers = {
  // Extract  data from API response
  extractOrganizationData: (response) => {
    if (response.data && response.success) {
      return response.data;
    }
    throw new Error(response.data?.message || "Unknown error occurred");
  },

  // Extract paginated list from API response
  extractOrganizationList: (response) => {
    if (response.data && response.success) {
      const data = response.data;
      return {
        organizations: data.data || [],
        pagination: {
          current: data.current_page,
          pageSize: data.per_page,
          total: data.total,
          from: data.from,
          to: data.to,
          lastPage: data.last_page,
          nextPageUrl: data.next_page_url,
          prevPageUrl: data.prev_page_url,
        },
      };
    }
    throw new Error(response.data?.message || "Unknown error occurred");
  },

  // Format data for form submission
  formatOrganizationData: (formData) => {
    return {
      parent_id: formData.parent_id,
      name: formData.name?.trim(),
      description: formData.description?.trim(),
      status: formData.status ?? true,
      budget_code: formData.budget_code?.trim(),
      resident_count: formData.resident_count,
      household_count: formData.household_count,
    };
  },

  // Validate data before submission
  validateOrganizationData: (organizationData) => {
    const errors = {};

    if (!organizationData.name?.trim()) {
      errors.name = "Tên đơn vị không được để trống";
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },
};

// Error handling wrapper
export const withErrorHandling = (apiFunction) => {
  return async (...args) => {
    try {
      const response = await apiFunction(...args);
      return response.data;
    } catch (error) {
      console.error("API Error:", error);

      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;
        switch (status) {
          case 400:
            throw new Error(data.message || "Dữ liệu không hợp lệ");
          case 401:
            throw new Error("Bạn cần đăng nhập để thực hiện thao tác này");
          case 403:
            throw new Error("Bạn không có quyền thực hiện thao tác này");
          case 404:
            throw new Error("Không tìm thấy khách hàng");
          case 422: {
            // StoreOrganizationRequest/UpdateOrganizationRequest trả
            // {success:false, errors:{field:[...]}} và KHÔNG có `message`, nên chỉ đọc
            // data.message thì lỗi theo trường bị nuốt mất, người dùng chỉ thấy câu
            // chung chung. Gắn kèm để form hiện đúng ô.
            const validationError = new Error(data.message || "Dữ liệu không hợp lệ");
            validationError.fieldErrors = data.errors || null;
            throw validationError;
          }
          case 500:
            throw new Error(data.message || "Lỗi server, vui lòng thử lại sau");
          default:
            throw new Error(data.message || "Đã có lỗi xảy ra");
        }
      } else if (error.request) {
        // Network error
        throw new Error("Không thể kết nối đến server");
      } else {
        // Other error
        throw new Error(error.message || "Đã có lỗi xảy ra");
      }
    }
  };
};

// Wrapped API functions with error handling
export const organizationAPIWithErrorHandling = {
  getOrganizations: withErrorHandling(organizationAPI.getOrganizations),
  getOrganizationById: withErrorHandling(organizationAPI.getOrganizationById),
  createOrganization: withErrorHandling(organizationAPI.createOrganization),
  updateOrganization: withErrorHandling(organizationAPI.updateOrganization),
  deleteOrganization: withErrorHandling(organizationAPI.deleteOrganization),
  updateStatus: withErrorHandling(organizationAPI.updateStatus),
  getTreeStructure: withErrorHandling(organizationAPI.getTreeStructure),
  getOrgCustomers: withErrorHandling(organizationAPI.getOrgCustomers),
  attachCustomers: withErrorHandling(organizationAPI.attachCustomers),
  detachCustomers: withErrorHandling(organizationAPI.detachCustomers),
  getAvailableCustomers: withErrorHandling(organizationAPI.getAvailableCustomers),
  getOrgCollections: withErrorHandling(organizationAPI.getOrgCollections),
  getTotalCollections: withErrorHandling(organizationAPI.getTotalCollections),
  getCustomerAllByOrg: withErrorHandling(organizationAPI.getCustomerAllByOrg),
  getCustomerAllByOrgs: withErrorHandling(organizationAPI.getCustomerAllByOrgs),
};

export default organizationAPI;
